import type { CSSProperties, ReactElement } from "react"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import type { SearchDomainExtension } from "@gatsby-plugin-generic-page/fragments/searchSection"
import { getBorderColorVar } from "@gatsby-plugin-generic-page/fragments/udsBorderColor"
import Button from "@ionos-web-design-system/react/button"

export type DomainExtensionButtonsProps = {
  extensions: SearchDomainExtension[]
  selected: string | null
  onSelect: (extension: string) => void
}

const MOBILE_EXTENSIONS_LIMIT = 2

// On small screens only the first MOBILE_EXTENSIONS_LIMIT extensions render — but
// the selected one (e.g. a CMS-configured default) must always stay visible,
// otherwise there's no button left to deselect it with.
function getVisibleExtensions(
  extensions: SearchDomainExtension[],
  selected: string | null
): SearchDomainExtension[] {
  const selectedExtension = extensions.find(
    ({ domain }) => domain[0].flatData.extension === selected
  )

  if (!selectedExtension) {
    return extensions.slice(0, MOBILE_EXTENSIONS_LIMIT)
  }

  const rest = extensions.filter(
    ({ domain }) => domain[0].flatData.extension !== selected
  )

  return [selectedExtension, ...rest].slice(0, MOBILE_EXTENSIONS_LIMIT)
}

// UDS renders the secondary button border as a box-shadow driven by this custom
// property — overriding it here is the only way to force --border-base without
// touching the design system.
const INACTIVE_BORDER_STYLE = {
  "--_border-color": "var(--border-base)",
} as CSSProperties

function DomainExtensionButtons({
  extensions,
  selected,
  onSelect,
}: DomainExtensionButtonsProps): ReactElement<DomainExtensionButtonsProps> {
  const isSmallScreen = useBreakpoint("small", "medium")

  const visibleExtensions = isSmallScreen
    ? getVisibleExtensions(extensions, selected)
    : extensions

  return (
    <div className="flex items-center gap-gap-8 mr-3">
      {visibleExtensions.map(({ domain, activeColor }) => {
        const { id, extension } = domain[0].flatData
        const isActive = selected === extension
        // getBorderColorVar returns "" for an explicit "none" — treated the same as
        // "not configured" here, so both fall back to the UDS brand active look.
        const activeColorVar = getBorderColorVar(activeColor) || undefined

        return (
          <Button
            key={id}
            // Falls back to the UDS "brand" solid look whenever no CMS color is
            // configured for this extension, so the active state is always visible.
            concept={isActive && !activeColorVar ? "brand" : "monochrome"}
            variant={isActive ? "primary" : "secondary"}
            size="small"
            aria-pressed={isActive}
            // Design wants the inactive (secondary) button's hover state to match
            // the UDS tertiary variant's hover colors, not its own secondary ones —
            // text color must follow too, otherwise it's invisible on the new background.
            className={
              !isActive
                ? "hover:bg-surface-button-mono-tertiary-hover hover:text-button-mono-tertiary-hover"
                : undefined
            }
            onClick={() => onSelect(extension)}
            style={
              isActive && activeColorVar
                ? {
                    backgroundColor: activeColorVar,
                    borderColor: activeColorVar,
                    color: "#ffffff",
                  }
                : !isActive
                  ? INACTIVE_BORDER_STYLE
                  : undefined
            }
          >
            {`.${extension}`}
          </Button>
        )
      })}
    </div>
  )
}

export default DomainExtensionButtons
